import { Injectable, NgZone } from '@angular/core';
import { fromEvent, merge, Subscription, timer } from 'rxjs';
import { switchMap, filter } from 'rxjs/operators';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';
import { AuthService } from './auth.service';
import { NotificationService } from './notification.service';

/**
 * Service global de gestion d'inactivité utilisateur.
 * Si aucun événement utilisateur n'est détecté pendant environment.idleTimeoutMs,
 * l'utilisateur est automatiquement déconnecté.
 */
@Injectable({
  providedIn: 'root'
})
export class IdleTimeoutService {
  private readonly idleTimeMs = environment.idleTimeoutMs;

  private activitySubscription: Subscription | null = null;
  private idleTimerSubscription: Subscription | null = null;
  private initialized = false;

  constructor(
    private ngZone: NgZone,
    private authService: AuthService,
    private router: Router,
    private notificationService: NotificationService
  ) {}

  /**
   * À appeler une seule fois (par exemple dans AppComponent)
   * pour démarrer la surveillance globale de l'activité.
   */
  init(): void {
    if (this.initialized) {
      return;
    }
    this.initialized = true;

    // Lier la logique au statut d'authentification :
    // on ne surveille l'inactivité que si l'utilisateur est connecté.
    this.authService.isAuthenticated$.subscribe((isAuth) => {
      if (isAuth) {
        this.startWatching();
      } else {
        this.stopWatching();
      }
    });
  }

  private startWatching(): void {
    if (this.activitySubscription) {
      return;
    }

    this.ngZone.runOutsideAngular(() => {
      const events$ = merge(
        fromEvent(window, 'mousemove'),
        fromEvent(window, 'keydown'),
        fromEvent(window, 'click'),
        fromEvent(window, 'scroll'),
        fromEvent(window, 'touchstart')
      );

      // À chaque activité, on (re)lance le timer configuré dans l'environnement
      this.activitySubscription = events$
        .pipe(
          filter(() => this.authService.isLoggedIn()),
          switchMap(() => {
            this.clearIdleTimer();
            return timer(this.idleTimeMs);
          })
        )
        .subscribe(() => {
          this.handleIdleTimeout();
        });
    });
  }

  private stopWatching(): void {
    if (this.activitySubscription) {
      this.activitySubscription.unsubscribe();
      this.activitySubscription = null;
    }
    this.clearIdleTimer();
  }

  private clearIdleTimer(): void {
    if (this.idleTimerSubscription) {
      this.idleTimerSubscription.unsubscribe();
      this.idleTimerSubscription = null;
    }
  }

  private handleIdleTimeout(): void {
    const idleMessage = this.getIdleTimeoutMessage();

    // On exécute dans la zone Angular pour pouvoir naviguer et afficher des notifications.
    this.ngZone.run(() => {
      // Déconnexion côté backend (si possible), mais on ne bloque pas sur l'erreur.
      this.authService.logout().subscribe({
        next: () => {
          this.notificationService.error('Session expirée', idleMessage);
          this.router.navigate(['/auth/login']);
        },
        error: () => {
          // Même en cas d’erreur backend, on nettoie localement et on redirige.
          this.notificationService.error('Session expirée', idleMessage);
          this.router.navigate(['/auth/login']);
        }
      });
    });
  }

  private getIdleTimeoutMessage(): string {
    const minutes = Math.round(this.idleTimeMs / (60 * 1000));
    const label = minutes <= 1 ? '1 minute' : `${minutes} minutes`;
    return `Vous avez été déconnecté après ${label} d’inactivité.`;
  }
}

